
import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Embryo, EmbryoResultsDetails, Patient, Result, GradingHistoryItem, RedirectSessionDetails } from './EmbryoGradingAi.helper';


interface GradingHistoryContextType {
  loadingHistory: boolean;
  setLoadingHistory: React.Dispatch<React.SetStateAction<boolean>>;

  isGradingGenerated: boolean;
  setIsGradingGenerated: React.Dispatch<React.SetStateAction<boolean>>;

  gradingHistoriesList: GradingHistoryItem[];
  setGradingHistoriesList: React.Dispatch<React.SetStateAction<GradingHistoryItem[]>>;

  selectedGradingHistory: GradingHistoryItem | null;
  setSelectedGradingHistory: React.Dispatch<React.SetStateAction<GradingHistoryItem | null>>;
  isGradingHistoryOpen: boolean;
  toggleGradingHistoryOpen: (status?: boolean) => void;
  selectedPatientsForEmbryoGrading: Patient[];
  setSelectedPatientsForEmbryoGrading: React.Dispatch<React.SetStateAction<Patient[]>>;
  selectedPatientForGrading: Patient | null;
  setSelectedPatientForGrading: React.Dispatch<React.SetStateAction<Patient | null>>;

  selectedEmbryosGrading: Embryo[];
  setSelectedEmbryosGrading: React.Dispatch<React.SetStateAction<Embryo[]>>;

  selectedEmbryoForGrading: Embryo | null;
  setSelectedEmbryoForGrading: React.Dispatch<React.SetStateAction<Embryo | null>>;
 
  selectedEmbryoResult: EmbryoResultsDetails | null;
  setSelectedEmbryoResult: React.Dispatch<React.SetStateAction<EmbryoResultsDetails | null>>;

  resultListForSelectedPatient: Result[];
  setResultListForSelectedPatient: React.Dispatch<React.SetStateAction<Result[]>>;

  selectedResultFromResultLList: Result | null;
  setSelectedResultFromResultList: React.Dispatch<React.SetStateAction<Result | null>>;

  showFullResult: boolean;
  setShowFullResult: React.Dispatch<React.SetStateAction<boolean>>;

  isEmbryoGradingDetailsOpen: boolean;
  toggleEmbryoGradingDetailsOpen: (status?: boolean) => void;
  isResultAvailable : boolean;
  toggleResultAvailable: (status?: boolean) => void;

  selectedSessionDetails: RedirectSessionDetails | null;
  setSelectedSessionDetails: React.Dispatch<React.SetStateAction<RedirectSessionDetails | null>>;
}

const GradingHistoryContext = createContext<GradingHistoryContextType | undefined>(undefined);

export const useGradingHistory = () => {
  const context = useContext(GradingHistoryContext);
  if (!context) {
    throw new Error('useGradingHistory must be used within a GradingHistoryProvider');
  }
  return context;
};

export const GradingHistoryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {


  const [isGradingHistoryOpen, setIsGradingHistoryOpen] = useState(false);  // for result history

  const [isEmbryoGradingDetailsOpen, setIsEmbryoGradingDetailsOpen] = useState(false);  // for selected patients in embryo grading

  const [isResultAvailable, setIsResultAvailable] = useState(false);  // if result is not available then show loader



  const [isGradingGenerated, setIsGradingGenerated] = useState(false);  // if result is not generated then show loader


  const [selectedPatientsForEmbryoGrading, setSelectedPatientsForEmbryoGrading] = useState<Patient[]>([]); // patients selected for embryo grading
  const [selectedPatientForGrading, setSelectedPatientForGrading] = useState<Patient | null>(null); // currently selected patient for grading

  // State for grading history selection
  const [gradingHistoriesList, setGradingHistoriesList] = useState<GradingHistoryItem[]>([]); // grading histories List
  const [selectedGradingHistory, setSelectedGradingHistory] = useState<GradingHistoryItem | null>(null); // selected grading history

  const [selectedEmbryosGrading, setSelectedEmbryosGrading] = useState<Embryo[]>([]); // embryos List selected for grading
  const [selectedEmbryoForGrading, setSelectedEmbryoForGrading] = useState<Embryo | null>(null);  // selected Embryo for grading

  // State for selectedEmbryoResult
  const [selectedEmbryoResult, setSelectedEmbryoResult] = useState<EmbryoResultsDetails | null>(null);
  // State for resultListForSelectedPatient
  const [resultListForSelectedPatient, setResultListForSelectedPatient] = useState<Result[]>([]);
  const [selectedResultFromResultLList, setSelectedResultFromResultList] = useState<Result | null>(null);  // selected Result from result list

  const [showFullResult, setShowFullResult] = useState(false);  // for show full result of single patient


  const [loadingHistory, setLoadingHistory] = useState(false);  // for loading history


  const [selectedSessionDetails, setSelectedSessionDetails] = useState<RedirectSessionDetails | null>(null);  // session details from redirect


  const toggleGradingHistoryOpen = (status?: boolean) => {  
    if (status !== undefined) {
      setIsGradingHistoryOpen(status);
    } else {
      setIsGradingHistoryOpen((prev) => !prev);
    }
  };

  const toggleEmbryoGradingDetailsOpen = (status?: boolean) => {
    if (status !== undefined) {
      setIsEmbryoGradingDetailsOpen(status);   // Set to the exact status provided
    } else {
      setIsEmbryoGradingDetailsOpen((prev) => !prev);  // Toggle when no status provided
    }
  };

  const toggleResultAvailable = (status?: boolean) => {
    if (status !== undefined) {
      setIsResultAvailable(status);   
    } else {
      setIsResultAvailable((prev) => !prev); 
    }
  };

  return (
    <GradingHistoryContext.Provider value={{ 
      isGradingHistoryOpen, 
      toggleGradingHistoryOpen, 
      selectedPatientsForEmbryoGrading, 
      setSelectedPatientsForEmbryoGrading, 
      selectedPatientForGrading,
      setSelectedPatientForGrading,
      selectedEmbryosGrading,
      setSelectedEmbryosGrading,
      selectedEmbryoResult,
      setSelectedEmbryoResult,
      resultListForSelectedPatient,
      setResultListForSelectedPatient,
      selectedResultFromResultLList,
      setSelectedResultFromResultList,
      showFullResult,
      setShowFullResult,
      isEmbryoGradingDetailsOpen, 
      toggleEmbryoGradingDetailsOpen,
      isResultAvailable,
      toggleResultAvailable,

      gradingHistoriesList,
      setGradingHistoriesList,

      selectedGradingHistory,
      setSelectedGradingHistory,

      loadingHistory,
      setLoadingHistory,

      selectedEmbryoForGrading,
      setSelectedEmbryoForGrading,

      isGradingGenerated,
      setIsGradingGenerated,

      selectedSessionDetails,
      setSelectedSessionDetails,
    }}>
      {children}
    </GradingHistoryContext.Provider>
  );
};
